/**
 * Service layer for Chat Agent CRUD operations.
 * Calls the platform-api which bridges to LibreChat.
 */

import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';
import { getOrgNodeAccessConfig } from '@/services/admin/get-org-node-access-config';
import { filterToolGroupsByOrgAccess } from '@/services/chat-agents/helpers/filter-tool-groups';
import type {
  ChatAgent,
  ChatAgentCreateRequest,
  ChatAgentUpdateRequest,
  ChatAgentListResponse,
  ChatAgentResponse,
  ConversationSummary,
  LibreChatTokenResponse,
  ToolGroup,
  AvailableToolsResponse,
} from '@/types/chat-agent-types';

// Helper to unwrap apiRequest response which wraps data in { data: T } or { message, data: T }
function unwrap<T>(response: unknown): T | null {
  if (!response) return null;
  const res = response as Record<string, unknown>;
  if ('data' in res && res.data) return res.data as T;
  return response as T;
}

export async function listChatAgents(orgId: string): Promise<ChatAgent[]> {
  const response = await apiRequest<ChatAgentListResponse>({
    url: `/chat-agents/${orgId}`,
    method: 'GET',
    target: 'platform',
  });
  const data = unwrap<ChatAgentListResponse>(response);
  return data?.agents ?? [];
}

export async function getChatAgent(
  orgId: string,
  agentId: string,
): Promise<ChatAgent | null> {
  const response = await apiRequest<ChatAgentResponse>({
    url: `/chat-agents/${orgId}/${agentId}`,
    method: 'GET',
    target: 'platform',
  });
  const data = unwrap<ChatAgentResponse>(response);
  return data?.agent ?? null;
}

export async function createChatAgent(
  orgId: string,
  data: ChatAgentCreateRequest,
): Promise<ChatAgent | null> {
  const response = await apiRequest<ChatAgentResponse>({
    url: `/chat-agents/${orgId}`,
    method: 'POST',
    target: 'platform',
    body: data as unknown as Record<string, unknown>,
  });
  const result = unwrap<ChatAgentResponse>(response);
  return result?.agent ?? null;
}

export async function updateChatAgent(
  orgId: string,
  agentId: string,
  data: ChatAgentUpdateRequest,
): Promise<ChatAgent | null> {
  const response = await apiRequest<ChatAgentResponse>({
    url: `/chat-agents/${orgId}/${agentId}`,
    method: 'PUT',
    target: 'platform',
    body: data as unknown as Record<string, unknown>,
  });
  const result = unwrap<ChatAgentResponse>(response);
  return result?.agent ?? null;
}

export async function deleteChatAgent(
  orgId: string,
  agentId: string,
): Promise<void> {
  await apiRequest({
    url: `/chat-agents/${orgId}/${agentId}`,
    method: 'DELETE',
    target: 'platform',
  });
}

export async function disconnectSlack(
  orgId: string,
  agentId: string,
): Promise<ChatAgent | null> {
  const response = await apiRequest<ChatAgentResponse>({
    url: `/chat-agents/${orgId}/${agentId}/slack`,
    method: 'DELETE',
    target: 'platform',
  });
  const result = unwrap<ChatAgentResponse>(response);
  return result?.agent ?? null;
}

export async function deleteConversation(
  orgId: string,
  agentId: string,
  conversationId: string,
): Promise<void> {
  const response = await apiRequest({
    url: `/chat-agents/${orgId}/${agentId}/conversations/${conversationId}`,
    method: 'DELETE',
    target: 'platform',
  });
  if (response.detail) {
    throw new Error(response.detail);
  }
}

export async function getAgentChatHistory(
  orgId: string,
  agentId: string,
): Promise<{
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
  }>;
  conversationId: string | null;
  parentMessageId: string | null;
} | null> {
  const response = await apiRequest<{
    messages: Array<{
      id: string;
      role: string;
      content: string;
      timestamp: string;
    }>;
    conversationId: string | null;
    parentMessageId: string | null;
  }>({
    url: `/chat-agents/${orgId}/${agentId}/conversations`,
    method: 'GET',
    target: 'platform',
  });
  const data = unwrap<{
    messages: Array<{
      id: string;
      role: 'user' | 'assistant';
      content: string;
      timestamp: string;
    }>;
    conversationId: string | null;
    parentMessageId: string | null;
  }>(response);
  return data ?? null;
}

export async function listAgentConversations(
  orgId: string,
  agentId: string,
): Promise<ConversationSummary[]> {
  try {
    const response = await apiRequest<{
      conversations: ConversationSummary[];
    }>({
      url: `/chat-agents/${orgId}/${agentId}/conversations/list`,
      method: 'GET',
      target: 'platform',
    });
    const data = unwrap<{ conversations: ConversationSummary[] }>(response);
    return data?.conversations ?? [];
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: 'list-agent-conversations' },
    });
    return [];
  }
}

export async function getConversationMessages(
  orgId: string,
  agentId: string,
  conversationId: string,
): Promise<{
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
  }>;
  conversationId: string;
  parentMessageId: string | null;
} | null> {
  const response = await apiRequest<{
    messages: Array<{
      id: string;
      role: string;
      content: string;
      timestamp: string;
    }>;
    conversationId: string;
    parentMessageId: string | null;
  }>({
    url: `/chat-agents/${orgId}/${agentId}/conversations/${conversationId}`,
    method: 'GET',
    target: 'platform',
  });
  const data = unwrap<{
    messages: Array<{
      id: string;
      role: 'user' | 'assistant';
      content: string;
      timestamp: string;
    }>;
    conversationId: string;
    parentMessageId: string | null;
  }>(response);
  return data ?? null;
}

export async function getLibreChatToken(
  orgId: string,
): Promise<LibreChatTokenResponse | null> {
  const response = await apiRequest<LibreChatTokenResponse>({
    url: `/chat-agents/${orgId}/auth/token`,
    method: 'GET',
    target: 'platform',
  });
  return unwrap<LibreChatTokenResponse>(response);
}

/**
 * Fetch available MCP tool groups from the platform API.
 * The API dynamically discovers tools from the mcp-servers-runner.
 * When an orgId is provided, groups explicitly disallowed by the org's
 * node access config are filtered out (fail-open on config errors).
 */
export async function getAvailableToolGroups(
  orgId?: string,
): Promise<ToolGroup[]> {
  try {
    const response = await apiRequest<AvailableToolsResponse>({
      url: '/chat-agents/available-tools',
      method: 'GET',
      target: 'platform',
    });
    const data = unwrap<AvailableToolsResponse>(response);
    const groups = data?.tool_groups ?? [];
    if (!orgId) return groups;
    try {
      const config = await getOrgNodeAccessConfig(orgId);
      return filterToolGroupsByOrgAccess(groups, config);
    } catch (e) {
      Sentry.captureException(e, {
        extra: { context: 'get-available-tool-groups-org-filter', orgId },
      });
      return groups;
    }
  } catch (e) {
    console.warn('Failed to fetch available tool groups:', e);
    Sentry.captureException(e, {
      extra: { context: 'get-available-tool-groups' },
    });
    return [];
  }
}
